import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@store/policies/precisionPolicy/actions';
import { selector } from '@store/policies/precisionPolicy';
import Policies from './components/Policies';
import { IPolicyVersion, IPolicy } from './types';
import EventContext from './EventContext';
import EditModal from './components/EditModal';

interface IEditVersion {
  policy: IPolicy | null;
  version: IPolicyVersion | null;
}

const PrecisionPolicy = () => {
  const dispatch = useDispatch();
  const state = useSelector(selector);
  const {
    policies,
    can_edit,
    resources,
    modalVisible,
    submitting: confirmLoading,
    activeView,
  } = state;
  const [editVersion, setEditVersion] = useState<IEditVersion>({
    policy: null,
    version: null,
  });

  const deletePolicy = useCallback(
    (id) => {
      dispatch(actions.deletePolicy(id));
    },
    [dispatch],
  );

  const reorderPolicies = useCallback(
    (order) => {
      dispatch(actions.reorderPolicies(order));
    },
    [dispatch],
  );

  const updatePolicy = useCallback(
    (data) => {
      dispatch(actions.updatePolicy(data));
    },
    [dispatch],
  );

  const activatePolicy = useCallback(
    (id) => {
      dispatch(actions.activatePolicy(id));
    },
    [dispatch],
  );

  const clonePolicy = useCallback(
    (id) => {
      dispatch(actions.clonePolicy(id));
    },
    [dispatch],
  );

  const editPolicy = useCallback(
    (policy, version) => {
      setEditVersion({ policy, version });
      dispatch(actions.toggleModal(true));
    },
    [setEditVersion, dispatch],
  );

  const onCancelEdit = useCallback(() => {
    dispatch(actions.toggleModal(false));
    setTimeout(() => setEditVersion({ policy: null, version: null }), 500);
  }, [dispatch, setEditVersion]);

  useEffect(() => {
    dispatch(actions.init());
  }, [dispatch]);

  return (
    <EventContext.Provider
      value={{
        deletePolicy,
        activatePolicy,
        reorderPolicies,
        updatePolicy,
        clonePolicy,
        editPolicy,
      }}
    >
      <Policies
        key={JSON.stringify(policies)}
        policies={policies}
        resources={resources}
        canEdit={can_edit}
        activeView={activeView}
      />
      <EditModal
        key={JSON.stringify(editVersion.version)}
        confirmLoading={confirmLoading}
        policy={editVersion.policy as IPolicy}
        visible={modalVisible}
        onCancel={onCancelEdit}
        version={editVersion.version as IPolicyVersion}
        resources={resources}
      />
    </EventContext.Provider>
  );
};

export default PrecisionPolicy;
